/** @jsxImportSource @emotion/react */
import { faBarcodeRead } from "@fortawesome/pro-regular-svg-icons";
import dayjs from "dayjs";
import { useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import {
    ButtonProps,
    Modal, ModalBody, ModalFooter, ModalHeader
} from "reactstrap";
import { LAYER_STAGE, LAYER_TYPE } from "../../../../actions/Layers/constants";
import { useAddLayerAllocationMutation } from "../../../../hooks/mutations";
import LayerAutoComplete from "../../../Forms/LayerAutoComplete";
import { RadioButtons } from "../../../Forms/RadioButton";

import useConfig from "../../../../actions/Tenants/config/configHook";
import { toast } from "../../../../utils/toast";
import MetaForm from "../../../Forms/MetaForm";
import QRCodeScan from "../../../Forms/QRCodeScan";
import SingleSelect from "../../../Forms/SingleSelect";
import { searchLayers } from "../../../Forms/utils";
import { Button } from "../../../Helper/Button";
import useRipeningRoom, { useIsItemAllocated } from "../hooks/useRipeningRoom";


interface LayerItem {
    id?: string;
    type?: string;
    label?: string;
    fruit_type?: string;
}

// * Add pallets to this cell (pallets from outside the cell)
export function AddSinglePalletModal({ setModal, modalOpen }: { setModal: any, modalOpen: boolean }) {
    const { refreshAllocatedLayers } = useRipeningRoom();
    const [layer, setLayer] = useState<any>(null);
    const addLayerAllocationMutation = useAddLayerAllocationMutation();
    const config = useConfig();
    const params = useParams();
    const { location_id } = params;
    const tenantId = params.tenant_id as string;

    const handleScan = (result: string) => {
        /**
         * Scan the barcode and search for pallet with that label
         */
        const searchResult = (data: LayerItem[]) => {
            if (data.length === 1) {
                setLayer(data[0]);
            } else if (data.length > 1) {
                toast.info(`Multiple pallets found with label ${result}`);
            } else {
                // if not exsite we create a new layer
                toast.info("Pallet not found");
            }
        };


        const handleError = (error: Error) => {
            toast.error(error.message);
        };
        searchLayers(result, LAYER_TYPE.PALLET)
            .then(searchResult)
            .catch(handleError);

    };

    const addPallet = () => {

        if (!layer?.id) {
            return;
        }
        addLayerAllocationMutation.mutateAsync({ tenantId, layerId: layer.id, stage: LAYER_STAGE.RIPENING, locationId: location_id as string }).then(() => {
            refreshAllocatedLayers();
            setModal(false);
        });
    };
    return <Modal isOpen={modalOpen} size={"md"} toggle={() => setModal(false)}>
        <ModalHeader toggle={() => setModal(false)} >Add pallets</ModalHeader>
        <ModalBody>
            <div className="pb-2">
                <QRCodeScan onScan={handleScan} barcode_filter={config.barcode_filter} />
            </div>
            <div>
                <LayerAutoComplete creatable={false} type={LAYER_TYPE.PALLET} setLayer={setLayer} layer={layer} />
            </div>
        </ModalBody>
        <ModalFooter>
            <Button size="sm" color="light" onClick={() => setModal(false)}>Cancel</Button>
            <Button size="sm" color="primary" onClick={addPallet}>Add pallet</Button>
        </ModalFooter>
    </Modal>;
}

// * Move pallets out of this cell based on the selected
export function RemoveLayerModal({ setModal, modalOpen, selectedLayers, refreshAllocatedLayers, deselectAll }: { setModal: any, modalOpen: boolean, selectedLayers: string[], refreshAllocatedLayers: any, deselectAll: any }) {
    const params = useParams();
    const tenantId = params.tenant_id as string;
    const { locations, currentLocation } = useRipeningRoom();
    const [new_location_id, setNewLocationId] = useState(null);

    // * use value instead of location_id so SingleSelect can use the options
    const transformedLocations = locations.filter((i) => i.location_id !== currentLocation.location_id).map((cell) => ({
        ...cell,
        value: cell.location_id
    }));

    const addLayerAllocationMutation = useAddLayerAllocationMutation();

    const allocateLayerToLocation = useCallback(() => {
        const location = locations.find((cell) => cell.location_id === new_location_id);

        if (!location) {
            return;
        }

        const { stage, location_id, facility } = location;

        Promise.all(selectedLayers.map((layer_id) => addLayerAllocationMutation.mutateAsync({ tenantId, layerId: layer_id, stage, locationId: location_id, includeChildren: false, facility }))).then(() => {
            refreshAllocatedLayers();
            deselectAll();
            setModal(false);
        });

    }, [new_location_id]);

    return <Modal isOpen={modalOpen} size="md">
        <ModalHeader >Move {selectedLayers.length} pallets</ModalHeader>
        <ModalBody>
            {transformedLocations.length < 7 ? (
                <RadioButtons
                    name="location"
                    color="secondary"
                    current={new_location_id || ""}
                    options={transformedLocations}
                    onChange={(_, value) => setNewLocationId(value)}
                />
            ) : (
                <SingleSelect
                    field="location"
                    options={transformedLocations}
                    post_value={new_location_id || ""}
                    setValue={(_, value) => setNewLocationId(value)}
                    label={false}
                />
            )}
        </ModalBody>
        <ModalFooter>
            <Button color="primary" disabled={!new_location_id} onClick={() => allocateLayerToLocation() }>Allocate</Button>
            <Button color="light" onClick={() => setModal(false)}>Cancel</Button>
        </ModalFooter>
    </Modal>;

}

export function ScanToMoveLayerModal({ isInBottomBar, buttonProps }: { isInBottomBar?: boolean, buttonProps?: ButtonProps }) {
    const { refreshAllocatedLayers, pallets, locations, currentLocation } = useRipeningRoom();
    const params = useParams();
    const tenantId = params.tenant_id as string;
    const [modalOpen, setModal] = useState(false);
    const [query, setQuery] = useState<any>({});


    const isItemAllocatedOnDate = useIsItemAllocated();
    const today = dayjs();
    const activePallets = pallets.filter((p) => isItemAllocatedOnDate(p.id, today));

    const config = useConfig();

    // * use value instead of location_id so SingleSelect can use the options
    const transformedLocations = locations.filter((i) => i.location_id !== currentLocation.location_id).map((cell) => ({
        ...cell,
        value: cell.location_id
    }));

    const addLayerAllocationMutation = useAddLayerAllocationMutation();

    const handleScan = (result: string) => {
        /**
         * Scan the barcode and search for pallet with that label
         */
        const layer = activePallets.find((p) => p.label === result);

        if (!layer) {
            toast.info("Pallet not found");
            return;
        }

        setQuery((prev) => ({ ...prev, layer_id: layer.id }));

    };

    const allocateLayerToLocation = useCallback(() => {
        const location = locations.find((cell) => cell.location_id === query.new_location_id);

        if (!location) {
            return;
        }

        const { stage, location_id, facility } = location;
        const { layer_id } = query;


        addLayerAllocationMutation.mutateAsync({ tenantId, layerId: layer_id, stage, locationId: location_id, includeChildren: false, facility }).then(() => {
            refreshAllocatedLayers();
            // deselectAll();
            setModal(false);
        });

    }, [query.layer_id, query.new_location_id]);

    const form = [
        {
            type: "element",
            el: <QRCodeScan onScan={handleScan} barcode_filter={config.barcode_filter} />
        },
        {
            type: "radio",
            label: "Pallet",
            name: "layer_id",
            options: activePallets.map((p) => ({ label: p.label, value: p.id }))
        },
        {
            type: transformedLocations.length < 7 ? "radio" : "single-select",
            label: "New Location",
            name: "new_location_id",
            options: transformedLocations,
        },

    ];

    return <>
        <Modal isOpen={modalOpen} size="md">
            <ModalHeader>Scan to move pallets</ModalHeader>

            <ModalBody>
                <MetaForm
                    meta={form}
                    object={query}
                    config={config}
                    setValue={(field, value) => setQuery((prev) => ({ ...prev, [field]: value }))}
                />
            </ModalBody>

            <ModalFooter>
                <Button
                    color="primary"
                    disabled={!query.new_location_id || !query.layer_id}
                    onClick={() => allocateLayerToLocation()}
                >
                    Allocate
                </Button>

                <Button color="light" onClick={() => setModal(false)}>Cancel</Button>
            </ModalFooter>
        </Modal>

        <Button
            disabled={activePallets.length === 0}
            color="light"
            onClick={() => setModal(true)}
            icon={faBarcodeRead}
            isInBottomBar={isInBottomBar}
            {...buttonProps}
        >
            Scan to move
        </Button>
    </>;
}
